/**
 * Group layout editor for Orientation Assignment with target Cohort
 * (GROUP-ORIENTATION-PLAN decision 2026-09-18: groups are decided at
 * assign time). Default is "everyone individually" — the plain case stays
 * one click; "+ New group" adds an editable group with members from the
 * cohort roster, a name, a leader (defaulting to the current user) and
 * Managed / Auto. Pure controlled component: CohortBuilder owns the list.
 */
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '@/components/Icon';
import { MemberPicker } from '@/components/Cohorts/MemberPicker';
import {
  layoutLeaders,
  withLeader,
  type GroupModeTag,
  type LayoutGroup,
} from '@/lib/groupLayout';

export function GroupLayoutCard({
  rosterEmails,
  blockedReasons,
  siteEmails,
  currentUserEmail,
  groups,
  onChange,
}: {
  /** The selected cohort's FULL roster (blocked members render disabled). */
  rosterEmails: string[];
  /** email -> why this member can't be grouped (hover text on the chip). */
  blockedReasons: ReadonlyMap<string, string>;
  /** Site roster for the leader picker (any site user can lead). */
  siteEmails: string[];
  currentUserEmail: string;
  groups: LayoutGroup[];
  onChange: (groups: LayoutGroup[]) => void;
}) {
  const patch = (key: number, part: Partial<LayoutGroup>) =>
    onChange(groups.map((g) => (g.key === key ? { ...g, ...part } : g)));

  function addGroup() {
    const nextKey = groups.reduce((m, g) => Math.max(m, g.key), 0) + 1;
    const next: LayoutGroup = {
      key: nextKey,
      name: '',
      memberEmails: [],
      leaderEmail: currentUserEmail,
      modeTag: 'Managed',
      allowLateJoin: true,
      autoStallSeconds: 30,
    };
    // withLeader strips the default leader's chips from every group —
    // the leader and the members are disjoint sets (#170).
    onChange(withLeader([...groups, next], nextKey, next.leaderEmail, currentUserEmail));
  }

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>Group layout</h3>
          <div className="sub">
            {groups.length === 0 ? (
              <>
                Everyone gets the orientation individually. Add a group to
                put a subset into one shared session with a leader.
              </>
            ) : (
              <>
                Everyone not placed in a group is assigned individually. A
                leader on the roster takes no assignment — they lead, they
                are not graded.
              </>
            )}
          </div>
        </div>
        <button type="button" className="btn sm ghost" onClick={addGroup}>
          + New group
        </button>
      </div>
      {groups.map((g, i) => {
        // A member can sit in one group only: each editor offers the
        // roster minus the OTHER groups' members. Every layout LEADER is
        // excluded from every list — the module refuses a leader who is
        // a member of any group, so the chip is never offered (#170).
        const elsewhere = new Set(
          groups.filter((x) => x.key !== g.key).flatMap((x) => x.memberEmails),
        );
        const leaders = new Set(layoutLeaders(groups, currentUserEmail));
        return (
        <GroupEditor
          key={g.key}
          group={g}
          index={i}
          pickableEmails={rosterEmails.filter(
            (e) => !elsewhere.has(e) && !leaders.has(e.trim().toLowerCase()),
          )}
          blockedReasons={blockedReasons}
          siteEmails={siteEmails}
          currentUserEmail={currentUserEmail}
          onPatch={(part) => patch(g.key, part)}
          onLeaderChange={(email) =>
            onChange(withLeader(groups, g.key, email, currentUserEmail))
          }
          onRemove={() => onChange(groups.filter((x) => x.key !== g.key))}
        />
        );
      })}
    </div>
  );
}

function GroupEditor({
  group: g,
  index,
  pickableEmails,
  blockedReasons,
  siteEmails,
  currentUserEmail,
  onPatch,
  onLeaderChange,
  onRemove,
}: {
  group: LayoutGroup;
  index: number;
  pickableEmails: string[];
  blockedReasons: ReadonlyMap<string, string>;
  siteEmails: string[];
  currentUserEmail: string;
  onPatch: (part: Partial<LayoutGroup>) => void;
  /** Leader changes go through the layout-level helper: the new leader's
   *  member chips are dropped from every group (#170). */
  onLeaderChange: (email: string) => void;
  onRemove: () => void;
}) {
  return (
    <div
      style={{
        borderTop: '1px solid var(--border-soft, var(--border))',
        padding: '10px 14px',
        display: 'grid',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="text"
          className="inp"
          placeholder={`Group ${index + 1} name, e.g. "Monday AM"`}
          maxLength={120}
          value={g.name}
          onChange={(e) => onPatch({ name: e.target.value })}
          aria-label={`Group ${index + 1} name`}
          style={{ fontSize: 12, padding: '4px 8px', flex: 1 }}
        />
        {(['Managed', 'Auto'] as const).map((m: GroupModeTag) => (
          <button
            key={m}
            type="button"
            className={'chip-filter ' + (g.modeTag === m ? 'on' : '')}
            onClick={() => onPatch({ modeTag: m })}
            aria-pressed={g.modeTag === m}
            title={
              m === 'Managed'
                ? 'The leader starts, continues and stops the run.'
                : 'Starts when everyone is ready; advances when every connected member finishes the step, or after the stall countdown. The leader can still override, and does not have to be present.'
            }
          >
            {m}
          </button>
        ))}
        <button
          type="button"
          className="icon-btn"
          onClick={onRemove}
          aria-label={`Remove group ${g.name || index + 1}`}
          title="Remove this group — its members go back to individual assignment."
          style={{ width: 22, height: 22 }}
        >
          <Icon of={Icons.action.remove} size={14} />
        </button>
      </div>

      <label style={{ display: 'grid', gap: 3, fontSize: 12 }}>
        <span style={{ color: 'var(--muted)', fontSize: 11 }}>
          {g.modeTag === 'Auto'
            ? 'Leader — can override the run, is not graded, does not have to join. Any site user.'
            : 'Leader — drives the run, is not graded, can be any site user'}
        </span>
        <select
          className="inp"
          value={g.leaderEmail}
          onChange={(e) => onLeaderChange(e.target.value)}
          aria-label={`Group ${index + 1} leader`}
          style={{ fontSize: 12, padding: '4px 8px' }}
        >
          {[currentUserEmail, ...siteEmails.filter((e) => e !== currentUserEmail)].map(
            (e) => (
              <option key={e} value={e}>
                {e}
                {e === currentUserEmail ? ' (you)' : ''}
              </option>
            ),
          )}
        </select>
      </label>

      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <label
          style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}
          title="Off: a member who never started cannot enter once the run is Running (their picker row reads late join closed). A dropped member can always rejoin."
        >
          <input
            type="checkbox"
            checked={g.allowLateJoin}
            onChange={(e) => onPatch({ allowLateJoin: e.target.checked })}
            style={{ width: 14, height: 14 }}
          />
          Allow late joiners
        </label>
        {g.modeTag === 'Auto' ? (
          <label
            style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}
            title="Once the first member finishes a stop, the run moves on after this many seconds even if others are still answering (their open checks expire). 0 = wait forever."
          >
            Stall seconds
            <input
              type="number"
              className="inp"
              min={0}
              max={3600}
              value={g.autoStallSeconds}
              onChange={(e) => {
                const v = Number.parseInt(e.target.value, 10);
                onPatch({ autoStallSeconds: Number.isFinite(v) && v >= 0 ? v : 0 });
              }}
              aria-label={`Group ${index + 1} stall seconds (0 = never)`}
              style={{ width: 80, fontSize: 12, padding: '3px 6px' }}
            />
            <span style={{ color: 'var(--muted)', fontSize: 11 }}>0 = never</span>
          </label>
        ) : null}
      </div>

      <MemberPicker
        emails={pickableEmails}
        disabledReasons={blockedReasons}
        selected={new Set(g.memberEmails)}
        onToggle={(email) =>
          onPatch({
            memberEmails: g.memberEmails.includes(email)
              ? g.memberEmails.filter((e) => e !== email)
              : [...g.memberEmails, email],
          })
        }
      />
    </div>
  );
}
